import { z } from 'zod';
import { emailSchema } from './auth.schema';
import { staffRoleSchema } from './staff.schema';

/** Contracts for the staff Settings screens (Figma A16). Shared so the API can validate exactly what the forms do. */

export const GSTIN_LENGTH = 15;
// 2-digit state code, 10-character PAN, entity number, a literal Z, then a check character.
const GSTIN_PATTERN = /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

/** Blank means "not filled in yet" and is fine; anything typed has to be a real GSTIN. */
export const gstinSchema = z
  .string()
  .trim()
  .toUpperCase()
  .superRefine((value, ctx) => {
    if (value === '') return;
    if (value.length !== GSTIN_LENGTH) {
      ctx.addIssue({
        code: 'custom',
        message: `GSTIN should have ${GSTIN_LENGTH} characters. This one has ${value.length}.`,
      });
    } else if (!GSTIN_PATTERN.test(value)) {
      ctx.addIssue({
        code: 'custom',
        message: 'That doesn’t look like a GSTIN. Check it against your GST certificate.',
      });
    }
  });

/** Blank is fine, anything typed must pass `schema`: lets an admin fill the agency profile in one field at a time. */
const blankOr = (schema: z.ZodType<string>) =>
  z
    .string()
    .trim()
    .superRefine((value, ctx) => {
      if (value === '') return;
      const result = schema.safeParse(value);
      if (!result.success)
        for (const issue of result.error.issues) ctx.addIssue({ code: 'custom', message: issue.message });
    });

/**
 * A16.6 Agency profile. Shown on GST invoices, emails and the client portal footer.
 * Nothing is required: you can save one field (say the legal name) and leave the rest empty. What is filled in
 * still has to be valid, so a half-typed GSTIN or email is caught.
 */
export const agencyProfileSchema = z.object({
  legalName: z.string().trim().default(''),
  brandName: z.string().trim().default(''),
  gstin: gstinSchema.default(''),
  gstState: z.string().trim().default(''),
  address: z.string().trim().default(''),
  supportEmail: blankOr(emailSchema).default(''),
  supportPhone: blankOr(z.string().regex(/^\+?[\d\s-]{8,16}$/, 'Enter a phone number with the area code')).default(''),
  defaultVenue: z.string().trim().default(''),
  meetLinkForInPerson: z.boolean().default(true),
});
export type AgencyProfile = z.infer<typeof agencyProfileSchema>;
export type AgencyProfileInput = z.input<typeof agencyProfileSchema>;

/** A16.8 "Reset two-step": the reason is saved in the audit log and the admin must type RESET. */
export const RESET_CONFIRMATION = 'RESET';
export const resetTwoFactorInputSchema = z.object({
  reason: z.string().trim().min(10, 'Say why in a few words. It is saved in the audit log.'),
  confirmation: z.literal(RESET_CONFIRMATION, { error: `Type ${RESET_CONFIRMATION} to confirm` }),
  signOutEverywhere: z.boolean().default(true),
});
export type ResetTwoFactorInput = z.input<typeof resetTwoFactorInputSchema>;

/** A16.7 Security policy. Two-step for staff is off by default and has a grace period once turned on. */
export const SECURITY_GRACE_DAYS = [1, 3, 7, 14, 30] as const;
export const REAUTH_MINUTES = [5, 10, 15, 30] as const;
export const securityPolicySchema = z.object({
  requireTwoFactor: z.boolean().default(false),
  appliesTo: z.array(staffRoleSchema).min(1, 'Pick at least one role'),
  graceDays: z
    .number()
    .refine((d) => (SECURITY_GRACE_DAYS as readonly number[]).includes(d), 'Pick one of the options')
    .default(7),
  sessionDays: z.number().int().min(1).max(90).default(30),
  idleHours: z.number().int().min(1).max(72).default(12),
  reauthMinutes: z
    .number()
    .refine((m) => (REAUTH_MINUTES as readonly number[]).includes(m), 'Pick one of the options')
    .default(10),
  emailDomains: z.array(z.string().trim().toLowerCase().min(3)).min(1, 'Keep at least one staff email domain'),
  allowGoogleSignIn: z.boolean().default(true),
  lockAfterTries: z.number().int().min(3).max(10).default(5),
  lockMinutes: z.number().int().min(5).max(120).default(15),
});
export type SecurityPolicy = z.infer<typeof securityPolicySchema>;

/** A16.10 Feature flags: everyone / a share of clients / specific businesses / staff only. */
export const FLAG_AUDIENCES = ['everyone', 'share', 'businesses', 'staff'] as const;
export const flagAudienceSchema = z.enum(FLAG_AUDIENCES);
export type FlagAudience = z.infer<typeof flagAudienceSchema>;

export const featureFlagInputSchema = z
  .object({
    enabled: z.boolean(),
    audience: flagAudienceSchema,
    sharePercent: z.number().int().min(1, 'Pick at least 1%').max(100, 'Pick 100% or less').default(10),
    businessIds: z.array(z.string().min(1)).default([]),
    alwaysOnForStaff: z.boolean().default(true),
    /** Saved in the change history; asked for when the share goes up or the flag turns off. */
    reason: z.string().trim().optional(),
  })
  .refine((v) => v.audience !== 'businesses' || v.businessIds.length > 0, {
    message: 'Add at least one business',
    path: ['businessIds'],
  });
export type FeatureFlagInput = z.input<typeof featureFlagInputSchema>;

/** A16.11 Maintenance mode: now, scheduled, or now with an automatic end. Admins can always sign in. */
export const maintenanceWindowSchema = z
  .object({
    startsAt: z.iso.datetime({ offset: true }),
    endsAt: z.iso.datetime({ offset: true }),
  })
  .refine((v) => new Date(v.endsAt) > new Date(v.startsAt), {
    message: 'The end has to be after the start',
    path: ['endsAt'],
  });

export const maintenanceMessageSchema = z
  .string()
  .trim()
  .min(1, 'Write a short message')
  .max(280, 'Keep it under 280 characters');

export const maintenanceSettingsSchema = z.object({
  message: maintenanceMessageSchema,
  staffKeepWorking: z.boolean().default(false),
  emailWaitingClients: z.boolean().default(true),
});
export type MaintenanceSettings = z.infer<typeof maintenanceSettingsSchema>;

/** Phone is optional here; when given it needs the digits of a real number (landlines and +91 both fine). */
export const staffProfileFormSchema = z.object({
  fullName: z.string().trim().min(1, 'Enter your name'),
  displayName: z.string().trim().max(40, 'Keep it under 40 characters'),
  phone: z
    .string()
    .trim()
    .refine((v) => v === '' || /^\+?[\d\s-]{8,16}$/.test(v), 'Enter a phone number with the area code'),
  timezone: z.string().min(1),
  language: z.string().min(1),
  dateFormat: z.string().min(1),
  weekStartsOn: z.enum(['monday', 'sunday', 'saturday']),
});
export type StaffProfileForm = z.infer<typeof staffProfileFormSchema>;
